import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Label } from "./label";
import { Typography } from "./Typography";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { cn } from "../lib/utils";

export interface TimeZoneOptionItem {
  value: string;
  label: string;
  countryName?: string;
  offset: string;
  offsetMinutes: number;
  searchKeywords?: string;
}

export type TimezoneSelectProps = {
  label?: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string | { message?: string };
  className?: string;
  timeZones?: TimeZoneOptionItem[];
  helperText?: string;
};

const getLocalTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const findMatchingTimezone = (
  tzList: TimeZoneOptionItem[],
  target?: string,
): TimeZoneOptionItem | null => {
  if (!target) return null;
  const lower = target.toLowerCase();
  const suffix = lower.split("/").pop() || lower;

  let caseMatch: TimeZoneOptionItem | null = null;
  let suffixMatch: TimeZoneOptionItem | null = null;

  for (let i = 0; i < tzList.length; i++) {
    const tz = tzList[i];
    if (tz.value === target) return tz;

    const valLower = tz.value.toLowerCase();
    if (!caseMatch && valLower === lower) {
      caseMatch = tz;
    }
    if (
      !suffixMatch &&
      (tz.countryName?.toLowerCase() === suffix ||
        tz.searchKeywords?.toLowerCase().includes(suffix) ||
        valLower.includes(suffix))
    ) {
      suffixMatch = tz;
    }
  }

  return caseMatch || suffixMatch || null;
};

const timezoneFilter = (value: string, search: string): number => {
  const query = search.trim().toLowerCase();
  if (!query) return 1;

  const target = value.toLowerCase();
  if (target === query) return 1;

  const words = target.split(/[\s,()/_]+/);
  if (words.some((w) => w === query)) return 1;
  if (words.some((w) => w.startsWith(query))) return 0.8;
  if (target.includes(query)) return 0.6;

  const cleanQuery = query.replace(/[\s,()+-:_/]+/g, "");
  const cleanTarget = target.replace(/[\s,()+-:_/]+/g, "");
  if (cleanQuery && cleanTarget.includes(cleanQuery)) return 0.4;

  return 0;
};

const TimezoneSelect = memo(function TimezoneSelect({
  label = "Candidate Time Zone",
  required = false,
  value,
  onChange,
  placeholder = "Select time zone...",
  searchPlaceholder = "Search time zone or offset (e.g. Kolkata, Abidjan, +5:30)...",
  disabled = false,
  loading = false,
  error,
  className,
  timeZones = [],
  helperText,
}: TimezoneSelectProps) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Auto-scroll dropdown to display the selected timezone in the list
  useEffect(() => {
    if (!open) return;

    const timerId = setTimeout(() => {
      const selectedEl = listRef.current?.querySelector<HTMLElement>(
        "[data-selected-timezone='true']",
      );
      selectedEl?.scrollIntoView({ block: "start" });
    }, 100);

    return () => clearTimeout(timerId);
  }, [open, value]);

  // Auto-select user's local timezone if no value provided
  useEffect(() => {
    if (!value && onChange && timeZones.length > 0) {
      const localTz = getLocalTimeZone();
      const defaultOption =
        findMatchingTimezone(timeZones, localTz) ||
        timeZones.find((tz) => tz.value === "UTC") ||
        timeZones[0];
      if (defaultOption) {
        onChange(defaultOption.value);
      }
    }
  }, [value, onChange, timeZones]);

  const selectedOption = useMemo(
    () => findMatchingTimezone(timeZones, value),
    [timeZones, value],
  );

  const errorMessage = typeof error === "string" ? error : error?.message;

  const handleSelect = useCallback(
    (tzValue: string) => {
      onChange?.(tzValue);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <div className={cn("w-full space-y-1", className)}>
      {label && (
        <Label>
          {label} {required && <span className="text-destructive text-xl">*</span>}
        </Label>
      )}

      <Popover
        open={open}
        onOpenChange={(next) => !disabled && !loading && setOpen(next)}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            className={cn(
              "w-full justify-between font-normal border",
              !selectedOption && "text-muted-foreground",
              errorMessage
                ? "border-destructive focus-visible:ring-destructive"
                : "",
            )}
          >
            <span className="flex items-center gap-2 truncate">
              <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          data-radix-scroll-lock-ignore="true"
          className="p-0 w-(--radix-popover-trigger-width) shadow-lg border rounded-lg overflow-hidden"
          align="start"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <Command filter={timezoneFilter}>
            <CommandInput
              placeholder={searchPlaceholder}
              className="h-10 text-sm"
            />

            <CommandList
              ref={listRef}
              data-radix-scroll-lock-ignore="true"
              className="max-h-60 overflow-y-auto p-1 overscroll-contain"
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                No matching time zone found.
              </CommandEmpty>

              <CommandGroup>
                {timeZones.map((tz) => {
                  const isSelected = value === tz.value;
                  const displayTitle = tz.countryName || tz.value;

                  return (
                    <CommandItem
                      key={tz.value}
                      value={`${tz.label} ${displayTitle} ${tz.searchKeywords || ""}`}
                      data-selected-timezone={isSelected ? "true" : undefined}
                      onSelect={() => handleSelect(tz.value)}
                      className={cn(
                        "flex items-center justify-between px-2.5 py-2 text-xs sm:text-sm rounded-md cursor-pointer transition-colors",
                        isSelected
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-accent",
                      )}
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 font-mono font-medium">
                          {tz.offset}
                        </span>
                        <span className="truncate text-left font-normal text-foreground">
                          {displayTitle}
                        </span>
                      </div>
                      <Check
                        className={cn(
                          "size-4 shrink-0 ml-2",
                          isSelected ? "opacity-100 text-primary" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {helperText && !errorMessage && (
        <span className="text-xs text-muted-foreground">{helperText}</span>
      )}

      {errorMessage && (
        <Typography variant="error" className="text-xs text-destructive">
          {errorMessage}
        </Typography>
      )}
    </div>
  );
});

export default TimezoneSelect;
